import React from 'react';

import {
  useTable,
  useFlexLayout,
  useSortBy,
  useResizeColumns,
  useExpanded,
  useRowSelect,
  useRowState,
  usePagination,
  TableInstance,
  PluginHook,
  IdType,
  TableOptions,
} from 'react-table';
import { useSticky } from 'react-table-sticky';

import { isNullOrUndefined } from '@adsk/alloy-react-helpers';

import { TABLE_SIZES } from './consts';
import createControlledStateHook from './createControlledStateHook';
import createSelectableColumn from './createSelectableColumn';
import { useReorderableColumns } from './pluginHooks/useReorderableColumns';
import { useSingleRowSelect } from './pluginHooks/useSingleRowSelect';
import { useStoredColumnState } from './pluginHooks/useStoredColumnState';
import { useTableSizeTheme } from './pluginHooks/useTableSizeTheme';
import remapColumns from './remapColumns';
import {
  createStateReducer,
  OnAction,
  observeAction,
  ensureToggleSortByDirection,
  disallowDisabledRowSelection,
} from './stateReducer';
import { TableSize, Column } from './types';

type Pagination = {
  isPaginatable?: boolean;
  manualPagination?: boolean;
  pageSize?: number;
  pageIndex?: number;
  totalDataCount?: number;
};
type Selectable = {
  isSingleSelectable?: boolean;
  isMultiSelectable?: boolean;
};
export type SetTableOptions<D extends Record<string, unknown>> = (
  opts: TableOptions<D>
) => TableOptions<D>;
export type SetPluginHooks<D extends Record<string, unknown>> = (
  hooks: PluginHook<D>[]
) => PluginHook<D>[];
type Table<D extends Record<string, unknown>> = {
  columns: Column<D>[];
  data?: D[];
  selectedRowIds?: Record<IdType<D>, boolean>;
  disabledRowIds?: Record<IdType<D>, boolean>;
  onAction?: OnAction<D>;
  defaultSize?: TableSize;
  isSortable?: boolean;
  isResizable?: boolean;
  isColumnReorderable?: boolean;
  isExpandable?: boolean;
  storageKey?: string;
  setTableOptions?: SetTableOptions<D>;
  setPluginHooks?: SetPluginHooks<D>;
};
export type UseTable<D extends Record<string, unknown>> = Table<D> &
  Selectable &
  Pagination;

const defaultData = [] as unknown;
const useTableHook = <
  D extends Record<string, unknown> = Record<string, unknown>
>({
  columns: columnsProp,
  data,
  defaultSize = TABLE_SIZES.MEDIUM,
  isSortable,
  isResizable,
  isExpandable,
  isPaginatable,
  isColumnReorderable,
  isSingleSelectable,
  isMultiSelectable,
  storageKey,
  pageSize = 50,
  pageIndex,
  manualPagination,
  totalDataCount,
  selectedRowIds,
  disabledRowIds,
  onAction,
  setTableOptions = (opts) => opts,
  setPluginHooks = (hooks) => hooks,
}: UseTable<D>): TableInstance<D> => {
  data ||= defaultData as D[];
  const columns = React.useMemo(
    () => remapColumns<D>(columnsProp),
    [columnsProp]
  );
  const pluginHooks: (PluginHook<D> | false | undefined)[] = [
    useRowState,
    useFlexLayout,
    useSticky,
    useTableSizeTheme,
    isSortable && useSortBy,
    isResizable && useResizeColumns,
    isExpandable && useExpanded,
    isPaginatable && usePagination,
    isColumnReorderable && useReorderableColumns,
    !!storageKey && useStoredColumnState,
    isSingleSelectable && (useSingleRowSelect as PluginHook<D>),
    isMultiSelectable && useRowSelect,
    isMultiSelectable &&
      createSelectableColumn<D>({
        defaultSize,
        columns,
        isPaginatable,
      }),
  ].filter(Boolean);

  if (typeof totalDataCount === 'undefined') {
    totalDataCount = data.length;
  }
  const rowState = React.useMemo(() => {
    return disabledRowIds
      ? Object.keys(disabledRowIds).reduce(
          (obj, key) => ({ ...obj, [key]: { isDisabled: true } }),
          {}
        )
      : undefined;
  }, [disabledRowIds]);

  const instance = useTable<D>(
    setTableOptions({
      defaultColumn: { minWidth: 52, width: 150 },
      columns,
      data,
      disableSortBy: !isSortable,
      disableResizing: !isResizable,
      useControlledState: createControlledStateHook({
        pageSize,
        pageIndex,
        totalDataCount,
        selectedRowIds,
      }),
      stateReducer: createStateReducer<D>(
        disallowDisabledRowSelection(),
        ensureToggleSortByDirection(),
        observeAction({ onAction })
      ),
      manualPagination,
      pageCount: manualPagination
        ? Math.ceil(totalDataCount / pageSize)
        : undefined,
      initialState: {
        ...(isNullOrUndefined(pageSize) ? {} : { pageSize }),
        ...(isNullOrUndefined(pageIndex) ? {} : { pageIndex }),
        ...(isNullOrUndefined(selectedRowIds) ? {} : { selectedRowIds }),
        ...(rowState
          ? { rowState: rowState as Record<IdType<D>, Record<string, unknown>> }
          : {}),
      },
      defaultSize,
      storageKey,
    }),
    ...setPluginHooks(pluginHooks as PluginHook<D>[])
  );
  return instance;
};

export default useTableHook;
